import React from 'react';

import {Box} from '@material-ui/core';
import {useHistory} from 'react-router';

import {useFlowFieldSearch, useSpinner} from 'hooks';
import {useSnackbar} from 'notistack';
import {EfeAccordion} from 'components';
import {Constants, HistoryRoutes, Messages, SnackbarVariants, ViewKeys} from 'commons/constants';
import {MainContent} from 'layouts/commons';
import {getBase64Extension, nextStep} from 'commons/utils/Functions';
import RepresentativePowerForm from './representative-power-form/RepresentativePowerForm';
import {
    AddressProofValues,
    Documents,
    EfeAccordionValues,
    EfeResponse,
    Field,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {DocumentServices} from 'services';

function RepresentativePoweAttorney() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const {expedientId, kycId, menu, personType, foreign, fatherKycId} = useMenuContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [powerAttorneyDoc, setPowerAttorneyDoc] = React.useState<any>(undefined);

    React.useEffect(() => {
        if (fatherKycId > 0 && ViewKeys.LEGAL_ATTORNEY_POWER === flow.data.expandKey) {
            setIsLoading(true);
            (async function () {
                try {
                    const response: EfeResponse<Documents> =
                        await DocumentServices.getDocumentByKycId(
                            Constants.DOC_ACTA_CONSTITUTIVA,
                            expedientId,
                            kycId
                        );
                    if (response && response.data) {
                        setPowerAttorneyDoc(response.data.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                }
            })();
        }
    }, []);

    async function onSubmit(values: AddressProofValues) {
        setIsLoading(true);
        try {
            const extension = getBase64Extension(values.file);
            const fileConverted = await (await fetch(values.file)).blob();
            const request: UploadDocumentServiceRequest = {
                documentKey: Constants.DOC_ACTA_CONSTITUTIVA,
                expedientId: expedientId,
                file: fileConverted,
                kycId: kycId,
                filename: `${Constants.DOC_ACTA_CONSTITUTIVA}.${extension}`,
            };

            const response: EfeResponse<any> = await DocumentServices.updateDocument(request);
            if (response && response.data) {
                enqueueSnackbar(Messages.FILE_UPLOAD_OK, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu);
                if (Constants.MODULE_STEP !== path) {
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                }
                setIsLoading(false);
            } else {
                setFields([...fields]);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.FILE_NOT_UPLOAD, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Poder notarial del Representante Legal o Apoderado Legal',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RepresentativePowerForm
                            fields={fields}
                            foreign={foreign}
                            personType={personType}
                            document={powerAttorneyDoc}
                            // eslint-disable-next-line react/jsx-handler-names
                            submitHandler={onSubmit}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.LEGAL_ATTORNEY_POWER === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion
                values={accordion}
                onChange={() => history.push(HistoryRoutes.REPRESENTATIVE_POWER)}
            />
        </MainContent>
    );
}

export default RepresentativePoweAttorney;
